import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, type TrendComponent, type TrendPoint } from '../../api/public'
import { Empty, Failed, Loading } from '../../components/Async'
import { useApi } from '../../components/useApi'
import { usePlayerParam, usePlayerPaths } from '../../components/playerPaths'
import { NotFound } from './NotFound'

/**
 * The Handicap Index over time (FR-039l, SC-013e).
 *
 * # A dated series over the whole record, not a current value
 *
 * The requirement is the series, and it is what makes "why did my index move?"
 * answerable — the question a member asks after a bad night. A single current
 * number cannot answer it, which is why `handicap_index_record` is append-only.
 *
 * # Inline SVG, and no charting library
 *
 * The constitution's dependency list is explicit and a chart library is not on
 * it. This is a line per player over a shared time axis: a path element and two
 * scales. Adding a library to draw it would be a dependency traceable to
 * nothing, and the simplest option that meets the requirement wins.
 *
 * The chart is also not the only representation — the same points are in a
 * table beneath it, which is what makes the page readable on a phone and to a
 * screen reader.
 */
export function HandicapTrend() {
  const { pathFor } = usePlayerPaths()
  const { playerId: playerParam } = useParams()
  // A slug in the path, a public id to the API — and the comparison list is
  // public ids throughout, because it goes straight into the request.
  const resolved = usePlayerParam(playerParam)
  const playerId = resolved ?? ''
  // **The full list is its own URL, not a toggle.** Every public view is
  // asserted to carry no button (FR-047c), and a link keeps the expanded list
  // shareable and the back button meaningful.
  const [params] = useSearchParams()
  const showAll = params.get('changes') === 'all'
  const [compare, setCompare] = useState<string[]>([])

  const roster = useApi((s) => api.players(s), [])
  const seasons = useApi((s) => api.seasons(s), [])
  const trend = useApi(playerId ? (s) => api.handicapTrend(playerId, compare, s) : null, [
    playerId,
    compare.join(','),
  ])

  if (resolved === undefined) return <Loading what="the trend" />
  if (resolved === null) return <NotFound />
  if (trend.state === 'loading') return <Loading what="the trend" />
  if (trend.state === 'error') return <Failed what="The handicap trend" error={trend.error} />

  const me =
    roster.state === 'ready' ? roster.data.find((p) => p.public_id === playerId) : undefined
  const others = roster.state === 'ready' ? roster.data.filter((p) => p.public_id !== playerId) : []

  const lines = [
    { id: playerId, name: me?.display_name ?? 'This player', series: trend.data.series },
    ...(trend.data.compare ?? []).map((c) => ({
      id: c.player_id,
      name: c.display_name,
      series: c.series,
    })),
  ].filter((l) => l.series.length > 0)

  return (
    <section>
      <h2>
        {me ? <Link to={pathFor(playerId)}>{me.display_name}</Link> : 'Handicap'} — index over time
      </h2>

      {lines.length === 0 ? (
        <Empty>No index has been computed yet. Three rounds are needed before there is one.</Empty>
      ) : (
        <Chart
          lines={lines}
          seasons={
            seasons.state === 'ready'
              ? // **A season with no schedule has no band.** Its dates are
                // derived from its weeks and are null until it has some, and a
                // band with no span is not a band. `flatMap` rather than a
                // filter so the narrowing is the compiler's, not an assertion.
                seasons.data.flatMap((s) =>
                  s.start_date && s.end_date
                    ? [{ id: s.id, name: s.name, start_date: s.start_date, end_date: s.end_date }]
                    : [],
                )
              : []
          }
        />
      )}

      {/* FR-039n: several players on a COMMON timeline, which is the only way a
          comparison means anything — two charts with different axes do not. */}
      {others.length > 0 && (
        <fieldset className="compare" data-testid="compare">
          <legend>Compare with</legend>
          {others.map((o) => (
            <label key={o.public_id}>
              <input
                type="checkbox"
                checked={compare.includes(o.public_id)}
                onChange={(e) =>
                  setCompare((current) =>
                    e.target.checked
                      ? [...current, o.public_id]
                      : current.filter((id) => id !== o.public_id),
                  )
                }
              />
              {o.display_name}
            </label>
          ))}
        </fieldset>
      )}

      <h3>{showAll ? 'Every change' : 'Latest changes'}</h3>
      {trend.data.series.length === 0 ? (
        <Empty>Nothing recorded yet.</Empty>
      ) : (
        <div className="scroll-x">
          <table className="table trend-table">
            <thead>
              <tr>
                <th scope="col">Effective</th>
                <th scope="col" className="numeric">
                  Index
                </th>
                <th scope="col" className="numeric">
                  Rounds
                </th>
                <th scope="col">Method</th>
              </tr>
            </thead>
            <tbody>
              {[...trend.data.series]
                .reverse()
                .slice(0, showAll ? undefined : LATEST_CHANGES)
                .map((p, i) => (
                  <tr key={`${p.effective_at}-${i}`}>
                    <td>
                      <time dateTime={p.effective_at}>{p.effective_at.slice(0, 10)}</time>
                    </td>
                    <td className="numeric strong">{p.index_value}</td>
                    {/* Shown so an index from four rounds is not read with the
                    confidence of one from twenty. */}
                    <td className="numeric">{p.rounds_counted}</td>
                    <td>
                      {p.method === 'declared'
                        ? 'declared starting index'
                        : p.method === 'reduced'
                          ? 'reduced record'
                          : 'lowest 8 of 20'}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
      {trend.data.series.length > LATEST_CHANGES && (
        <p className="note">
          <Link
            to={{ search: showAll ? '' : '?changes=all' }}
            preventScrollReset
            data-testid="changes-toggle"
          >
            {showAll
              ? `Show the latest ${LATEST_CHANGES}`
              : `Show all ${trend.data.series.length} changes`}
          </Link>
        </p>
      )}

      <Components trend={trend.data} />
    </section>
  )
}

/**
 * What the current index is made of (FR-039o, FR-027).
 *
 * # Named by round, not by id
 *
 * The first version listed each differential beside "round 1234" or "two nines
 * paired (rounds 1234)" -- database ids, and the second only ever named the
 * first nine. What a member recognises is a night: the date, the course and
 * what they shot. So each row names the round behind the figure, or both nines
 * of a pair with each nine's own differential, and links to the night.
 *
 * # Counted first, then the rest of the window
 *
 * The index is the average of the lowest eight of the latest twenty. The
 * counted rows come first, lowest first, which shows at a glance why they
 * count; the rest of the window follows, muted. The check FR-023d asks for --
 * that a figure can be verified by hand -- needs the inputs, not just the
 * answer.
 */
function Components({ trend }: { trend: { current_components: TrendComponent[] } }) {
  if (trend.current_components.length === 0) return null
  const byValue = (a: TrendComponent, b: TrendComponent) =>
    // Decimal strings compared for ORDER only; every figure shown is the string.
    Number(a.differential) - Number(b.differential)
  const counted = trend.current_components.filter((c) => c.included).sort(byValue)
  const inWindow = trend.current_components.filter((c) => !c.included).sort(byValue)

  return (
    <>
      <h3>What the current index is made of</h3>
      <p className="note">
        The lowest {counted.length} of the last {trend.current_components.length} differentials are
        averaged. A nine-hole round is paired with the next one into an eighteen-hole differential.
      </p>
      <div className="scroll-x">
        <table className="table component-table" data-testid="components">
          <thead>
            <tr>
              <th scope="col" className="numeric">
                Differential
              </th>
              <th scope="col">Played</th>
              <th scope="col">Course</th>
              <th scope="col" className="numeric">
                Score
              </th>
              <th scope="col" className="numeric">
                Nine's diff.
              </th>
            </tr>
          </thead>
          <ComponentRows title="Counted" rows={counted} />
          <ComponentRows title="In the window, not counted" rows={inWindow} muted />
        </table>
      </div>
    </>
  )
}

function ComponentRows({
  title,
  rows,
  muted,
}: {
  title: string
  rows: TrendComponent[]
  muted?: boolean
}) {
  if (rows.length === 0) return null
  return (
    <tbody className={muted ? 'component-muted' : undefined}>
      <tr className="component-group">
        <th scope="colgroup" colSpan={5}>
          {title} ({rows.length})
        </th>
      </tr>
      {rows.map((c, i) => {
        const pair = c.source_kind === 'differential_pair'
        return (
          <tr key={`${c.differential}-${i}`}>
            <td className="numeric strong">{c.differential}</td>
            <td>
              {c.rounds.map((r, j) => (
                <div key={j}>
                  {r.event_id ? (
                    <Link to={`/events/${r.event_id}`}>{r.played_date}</Link>
                  ) : (
                    r.played_date
                  )}
                </div>
              ))}
            </td>
            <td>
              {c.rounds.map((r, j) => (
                <div key={j}>
                  {r.course}
                  <span className="note-inline"> · {r.holes} holes</span>
                </div>
              ))}
            </td>
            <td className="numeric">
              {c.rounds.map((r, j) => (
                <div key={j}>{r.gross}</div>
              ))}
            </td>
            <td className="numeric note-inline">
              {pair ? c.rounds.map((r, j) => <div key={j}>{r.differential}</div>) : '—'}
            </td>
          </tr>
        )
      })}
    </tbody>
  )
}

/** How many of the most recent index changes the table shows before "show all". */
const LATEST_CHANGES = 5

/** A season's dates, for the bands the chart shades behind the lines. */
interface SeasonSpan {
  id: number
  name: string
  start_date: string
  end_date: string
}

interface Line {
  id: string
  name: string
  series: TrendPoint[]
}

const DAY = 86_400_000

/**
 * A line per player over one time axis, with gridlines and a hover readout.
 *
 * # A STEP line, because that is what an index is
 *
 * An index holds its value from the moment it is computed until the next round
 * changes it. Joining two records with a slope draws a value nobody ever played
 * off -- an index of 14.35 on the Tuesday between a 14.1 and a 14.6 -- so the
 * line runs flat and steps at each change, and the readout at any date is the
 * index that governed that date.
 *
 * # The hover layer finds the DATE, not the line
 *
 * A crosshair snaps to the nearest date any line changed on, and the tooltip
 * lists every player's index as it stood then -- nobody has to land the pointer
 * on a 2px line. The same readout is reachable from the keyboard (focus the
 * chart, then the arrow keys), and every value is in the table below as well:
 * the tooltip enhances, it never gates.
 *
 * # Seasons are part of the grid
 *
 * The league plays December to April, and an index sits still all summer. Each
 * season is a faint band from its start to its end, named under the axis, so a
 * flat stretch reads as the off-season rather than as a player who stopped
 * improving -- and a jump reads against the season it happened in.
 *
 * # Colours are the validated categorical order
 *
 * Run through the data-viz validator against this chart's white surface: every
 * hard check passes (worst adjacent CVD ΔE 9.1, normal-vision 19.6). Three of
 * the five sit under 3:1 contrast, which obliges relief -- the legend, the
 * tooltip and the table all name the values in text.
 */
function Chart({ lines, seasons }: { lines: Line[]; seasons: SeasonSpan[] }) {
  // **Drawn at its real width**, measured, rather than a fixed viewBox scaled
  // to fit: scaled, an 11px axis label became 21px on a wide monitor and the
  // 2px line a 4px one. The height is fixed; only the x-axis stretches.
  const wrap = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(720)
  useEffect(() => {
    const el = wrap.current
    if (!el) return
    const measure = () => setWidth(Math.max(320, Math.round(el.clientWidth)))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const height = 300
  const pad = { top: 16, right: 20, bottom: 34, left: 44 }
  const [hover, setHover] = useState<number | null>(null)

  // Values are decimal STRINGS and are parsed only to position a pixel. Every
  // number a reader sees comes from the string, never from this.
  const parsed = lines.map((l) => ({
    ...l,
    points: l.series
      .map((p) => ({ t: Date.parse(p.effective_at), v: Number(p.index_value), p }))
      .sort((a, b) => a.t - b.t),
  }))
  const all = parsed.flatMap((l) => l.points)
  const minT = Math.min(...all.map((p) => p.t))
  const maxT = Math.max(Math.max(...all.map((p) => p.t)), minT + DAY)
  const yTicks = niceTicks(Math.min(...all.map((p) => p.v)), Math.max(...all.map((p) => p.v)))
  const minV = yTicks[0]
  const maxV = yTicks[yTicks.length - 1]

  // The seasons that overlap the data, clipped to it.
  const bands = seasons
    .map((se) => ({
      id: se.id,
      name: se.name,
      from: Math.max(Date.parse(se.start_date), minT),
      to: Math.min(Date.parse(se.end_date) + DAY, maxT),
    }))
    .filter((b) => b.to > b.from)

  const plotW = width - pad.left - pad.right
  const plotH = height - pad.top - pad.bottom
  const x = (t: number) => pad.left + ((t - minT) / (maxT - minT)) * plotW
  const y = (v: number) => pad.top + (1 - (v - minV) / (maxV - minV || 1)) * plotH

  // Every date some line changed on: what the crosshair snaps between.
  const dates = [...new Set(all.map((p) => p.t))].sort((a, b) => a - b)
  const hoverT = hover === null ? null : dates[hover]
  const at = (points: typeof all, t: number) => {
    let found: (typeof all)[number] | undefined
    for (const p of points) if (p.t <= t) found = p
    return found
  }

  const nearest = (clientX: number, svg: SVGSVGElement) => {
    const box = svg.getBoundingClientRect()
    const vx = ((clientX - box.left) / box.width) * width
    const t = minT + ((vx - pad.left) / plotW) * (maxT - minT)
    let best = 0
    for (let i = 1; i < dates.length; i++) {
      if (Math.abs(dates[i] - t) < Math.abs(dates[best] - t)) best = i
    }
    return best
  }

  const readout =
    hoverT === null
      ? null
      : parsed
          .map((l, i) => ({ line: l, i, point: at(l.points, hoverT) }))
          .filter((r) => r.point !== undefined)
  const tipLeft = hoverT === null ? 0 : (x(hoverT) / width) * 100

  return (
    <div className="trend-chart-wrap" ref={wrap}>
      <div className="scroll-x">
        <div className="trend-plot">
          <svg
            className="trend-chart"
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            role="img"
            aria-label={`Handicap index over time for ${lines.map((l) => l.name).join(', ')}. Focus and use the arrow keys to read each change.`}
            data-testid="trend-chart"
            tabIndex={0}
            onPointerMove={(e) => setHover(nearest(e.clientX, e.currentTarget))}
            onPointerLeave={() => setHover(null)}
            onFocus={() => setHover((h) => h ?? dates.length - 1)}
            onBlur={() => setHover(null)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                e.preventDefault()
                const step = e.key === 'ArrowLeft' ? -1 : 1
                setHover((h) => Math.min(dates.length - 1, Math.max(0, (h ?? dates.length) + step)))
              } else if (e.key === 'Escape') {
                setHover(null)
              }
            }}
          >
            {/* Recessive hairline grid: index ticks across, dates down. */}
            {yTicks.map((v) => (
              <g key={`y${v}`}>
                <line
                  x1={pad.left}
                  x2={width - pad.right}
                  y1={y(v)}
                  y2={y(v)}
                  className={v === minV ? 'axis' : 'grid'}
                />
                <text x={pad.left - 8} y={y(v) + 4} className="axis-label" textAnchor="end">
                  {v.toFixed(yTicks.length > 1 && yTicks[1] - yTicks[0] < 1 ? 1 : 0)}
                </text>
              </g>
            ))}
            {/* Seasons: a faint band, a hairline at each end, the name under the
                axis. Clipped to the span of the data. */}
            {bands.map((b) => (
              <g key={`s${b.id}`} className="season-band">
                <rect x={x(b.from)} y={pad.top} width={x(b.to) - x(b.from)} height={plotH} />
                <line x1={x(b.from)} x2={x(b.from)} y1={pad.top} y2={height - pad.bottom} />
                <line x1={x(b.to)} x2={x(b.to)} y1={pad.top} y2={height - pad.bottom} />
                {x(b.to) - x(b.from) > 36 && (
                  <text
                    x={(x(b.from) + x(b.to)) / 2}
                    y={height - pad.bottom + 18}
                    className="axis-label"
                    textAnchor="middle"
                  >
                    {b.name}
                  </text>
                )}
              </g>
            ))}
            {bands.length === 0 &&
              timeTicks(minT, maxT).map((tick) => (
                <g key={`x${tick.t}`}>
                  <line
                    x1={x(tick.t)}
                    x2={x(tick.t)}
                    y1={pad.top}
                    y2={height - pad.bottom}
                    className="grid"
                  />
                  <text
                    x={x(tick.t)}
                    y={height - pad.bottom + 18}
                    className="axis-label"
                    textAnchor="middle"
                  >
                    {tick.label}
                  </text>
                </g>
              ))}

            {parsed.map((line, i) => {
              const pts = line.points
              const d = pts
                .map((p, j) => (j === 0 ? `M ${x(p.t)} ${y(p.v)}` : `H ${x(p.t)} V ${y(p.v)}`))
                .join(' ')
              const last = pts[pts.length - 1]
              return (
                <g key={line.id} className={`series series-${i}`}>
                  {pts.length > 1 && <path d={d} />}
                  {/* The end-dot: where the line stands now. */}
                  <circle cx={x(last.t)} cy={y(last.v)} r={4} className="end-dot" />
                </g>
              )
            })}

            {hoverT !== null && (
              <g className="crosshair" aria-hidden>
                <line x1={x(hoverT)} x2={x(hoverT)} y1={pad.top} y2={height - pad.bottom} />
                {readout?.map((r) => (
                  <circle
                    key={r.line.id}
                    cx={x(hoverT)}
                    cy={y(r.point!.v)}
                    r={4.5}
                    className={`hover-dot series-${r.i}`}
                  />
                ))}
              </g>
            )}
          </svg>

          {hoverT !== null && readout && readout.length > 0 && (
            <div
              className={`trend-tooltip${tipLeft > 60 ? ' trend-tooltip-left' : ''}`}
              style={{ left: `${tipLeft}%` }}
              role="status"
              data-testid="trend-tooltip"
            >
              <div className="trend-tooltip-date">{formatDate(hoverT)}</div>
              {readout.map((r) => (
                <div key={r.line.id} className={`trend-tooltip-row series-${r.i}`}>
                  <span className="line-key" aria-hidden />
                  <strong>{r.point!.p.index_value}</strong>
                  <span className="trend-tooltip-name">{r.line.name}</span>
                  {parsed.length === 1 && (
                    <span className="trend-tooltip-meta">
                      {r.point!.p.rounds_counted} rounds ·{' '}
                      {r.point!.t === hoverT ? 'changed this day' : 'unchanged'}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {lines.length > 1 && (
        <ul className="legend">
          {lines.map((line, i) => (
            <li key={line.id} className={`series-${i}`}>
              {line.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Clean index ticks -- steps of 0.5, 1, 2 or 5 -- spanning the values, about five. */
function niceTicks(min: number, max: number): number[] {
  const span = Math.max(max - min, 1)
  const step = [0.5, 1, 2, 5, 10].find((s) => span / s <= 6) ?? 10
  const lo = Math.floor(min / step) * step
  const hi = Math.ceil(max / step) * step
  const ticks: number[] = []
  for (let v = lo; v <= hi + step / 2; v += step) ticks.push(Math.round(v * 10) / 10)
  return ticks.length > 1 ? ticks : [lo, lo + step]
}

/**
 * Date ticks: the first of each year over a long record, the first of each
 * month over a season or less.
 */
function timeTicks(minT: number, maxT: number): { t: number; label: string }[] {
  const start = new Date(minT)
  const out: { t: number; label: string }[] = []
  const long = maxT - minT > 540 * DAY
  const d = long
    ? new Date(Date.UTC(start.getUTCFullYear() + 1, 0, 1))
    : new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 1))
  const everyOther = !long && maxT - minT > 200 * DAY
  let n = 0
  while (d.getTime() <= maxT) {
    if (!everyOther || n % 2 === 0) {
      out.push({
        t: d.getTime(),
        label: long
          ? String(d.getUTCFullYear())
          : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' }),
      })
    }
    n++
    if (long) d.setUTCFullYear(d.getUTCFullYear() + 1)
    else d.setUTCMonth(d.getUTCMonth() + 1)
  }
  return out
}

function formatDate(t: number): string {
  return new Date(t).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
}
